import { useId, useState } from "react";

import type { Role, RoleCatalog } from "@/api/access";
import {
  ROLE_DESCRIPTION,
  ROLE_LABEL,
  ROLE_ORDER,
  canonicalRoles,
  rolesIncludedBy,
} from "@/lib/editorAccess";
import { cn } from "@/lib/cn";

/**
 * The role checkboxes shared by Grant access and Edit access.
 *
 * Multiple roles are allowed where they add distinct capabilities (Library
 * Author beside Project Editor). A role the selection already includes —
 * either author role beside Clinical Manager, anything beside Administrator — is
 * neither silently sent nor left as a chip that confers nothing: selecting the
 * including role CLEARS it and says so in the live region, and while the
 * including role stays selected it is disabled with the reason beside it. The
 * inclusion facts come from the server's role catalog, never from a copy of
 * the matrix here; the service refuses a redundant set on its own if a browser
 * ever offered one.
 *
 * `locked` names roles this dialog may not change, with the reason to put on
 * the control — the administrator's own `admin` role, whose removal the
 * service refuses and this screen therefore does not offer.
 */
export function RoleSelector({
  selected,
  onChange,
  catalog,
  locked = {},
  describedBy,
  disabled = false,
}: {
  selected: readonly Role[];
  onChange: (next: Role[]) => void;
  catalog: RoleCatalog | undefined;
  locked?: Partial<Record<Role, string>>;
  /** Extra ids describing the whole group — a validation message, typically. */
  describedBy?: string;
  disabled?: boolean;
}) {
  const groupId = useId();
  const [announcement, setAnnouncement] = useState("");

  /**
   * The selected role that already carries `role`, if any — whether or not
   * `role` is itself selected. A cleared author role stays locked while
   * Clinical Manager is selected, so it cannot be re-added beside it; the
   * service would refuse that set, and the lock says why before it does.
   */
  function includedBy(role: Role): Role | undefined {
    return selected.find(
      (other) => other !== role && rolesIncludedBy(other, catalog).includes(role),
    );
  }

  function toggle(role: Role, checked: boolean) {
    if (!checked) {
      onChange(canonicalRoles(selected.filter((item) => item !== role)));
      setAnnouncement("");
      return;
    }
    const cleared = rolesIncludedBy(role, catalog).filter((included) =>
      selected.includes(included),
    );
    onChange(
      canonicalRoles([
        ...selected.filter((item) => !cleared.includes(item)),
        role,
      ]),
    );
    setAnnouncement(
      cleared.length > 0
        ? `${cleared.map((item) => ROLE_LABEL[item]).join(" and ")} ${
            cleared.length === 1 ? "is" : "are"
          } already included in ${ROLE_LABEL[role]}, so ${
            cleared.length === 1 ? "it was" : "they were"
          } cleared.`
        : "",
    );
  }

  return (
    <fieldset
      className="space-y-2"
      aria-describedby={describedBy}
      data-testid="role-selector"
      disabled={disabled}
    >
      <legend className="text-sm font-medium text-foreground">
        Editor roles
      </legend>
      <p className="text-xs text-muted-foreground">
        Choose every role this person needs. Roles that add nothing beside a
        selected role are cleared automatically.
      </p>
      <ul className="space-y-2">
        {ROLE_ORDER.map((role) => {
          const inputId = `${groupId}-${role}`;
          const descriptionId = `${inputId}-description`;
          const noteId = `${inputId}-note`;
          const including = includedBy(role);
          const lockReason = locked[role];
          const note = lockReason
            ? lockReason
            : including
              ? `Already included in ${ROLE_LABEL[including]}.`
              : null;
          const disabled = Boolean(lockReason) || Boolean(including);
          const checked = selected.includes(role);
          return (
            <li
              key={role}
              className={cn(
                "flex items-start gap-3 rounded-md border border-border bg-card p-3",
                disabled && "opacity-70",
              )}
            >
              <input
                id={inputId}
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0 accent-primary"
                checked={checked}
                disabled={disabled}
                aria-describedby={
                  note ? `${descriptionId} ${noteId}` : descriptionId
                }
                onChange={(event) => toggle(role, event.target.checked)}
              />
              <div className="min-w-0 space-y-1">
                <label
                  htmlFor={inputId}
                  className="block text-sm font-medium text-foreground"
                >
                  {ROLE_LABEL[role]}
                </label>
                <p id={descriptionId} className="text-xs text-muted-foreground">
                  {ROLE_DESCRIPTION[role]}
                </p>
                {note ? (
                  <p id={noteId} className="text-xs font-medium text-foreground">
                    {note}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      <p role="status" aria-live="polite" className="text-xs text-muted-foreground">
        {announcement}
      </p>
    </fieldset>
  );
}
